import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MailOutlined } from '@mui/icons-material';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';

/**
 * Page de demande de réinitialisation de mot de passe
 * Permet à l'utilisateur de saisir son email pour recevoir un lien de réinitialisation
 */
const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'CryptoNovaX - ' + t('auth.forgot.title');
  }, []);

  /**
   * Gère la soumission du formulaire de demande de réinitialisation
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const result = await authService.requestPasswordReset(email);
      setMessage(result.message);
    } catch (err: any) {
      setError(err.message || t('auth.forgot.errorMsg'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Valide le format de l'email
   */
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'stretch',
        background: '#ffffff',
        p: 0,
        m: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: '100vh',
          backgroundColor: '#ffffff',
          overflow: 'hidden',
        }}
      >
        {/* Left Column - Form */}
        <Box
          sx={{
            width: { xs: '100%', md: '50%' },
            p: { xs: 4, md: 6 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#ffffff',
          }}
        >
          <Box sx={{ width: '100%', maxWidth: '340px' }}>
            <Box sx={{ mb: 5, textAlign: 'center' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#666', 
                  mb: 0.5,
                  fontSize: '0.75rem',
                  fontWeight: 400,
                  letterSpacing: '0.02em',
                }}
              >
                {t('auth.forgot.welcome')}
              </Typography>
              <Typography 
                variant="h4" 
                sx={{ 
                  mb: 0, 
                  color: '#000',
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  letterSpacing: '-0.02em',
                }}
              >
                CryptoNovaX
              </Typography>
            </Box>

            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  color: '#000',
                  fontWeight: 600,
                  fontSize: '1.25rem',
                  mb: 1,
                }}
              >
                {t('auth.forgot.title')}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#666',
                  fontSize: '0.875rem',
                }}
              >
                {t('auth.forgot.input')}
              </Typography>
            </Box>

            {message && (
              <Alert
                severity="success"
                sx={{
                  mb: 2.5,
                  backgroundColor: 'rgba(76, 175, 80, 0.08)',
                  border: '1px solid rgba(76, 175, 80, 0.2)',
                  color: '#2e7d32',
                  borderRadius: '8px',
                }}
              >
                {message}
              </Alert>
            )}

            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 2.5,
                  backgroundColor: 'rgba(244, 67, 54, 0.08)',
                  border: '1px solid rgba(244, 67, 54, 0.2)',
                  color: '#d32f2f',
                  borderRadius: '8px',
                }}
              >
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                placeholder={t('auth.forgot.emailLabel')}
                error={email !== '' && !isValidEmail(email)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutlined sx={{ color: '#999', fontSize: '20px' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f8f8f8',
                    borderRadius: '8px',
                    height: '48px',
                    border: '1px solid #e0e0e0',
                    '& fieldset': {
                      border: 'none',
                    },
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#d0d0d0',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                      borderColor: '#1a1a1a',
                    },
                    '&.Mui-error': {
                      borderColor: '#d32f2f',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: '#000',
                    fontSize: '0.875rem',
                    '&::placeholder': {
                      color: '#999',
                      opacity: 1,
                    },
                  },
                }}
                helperText={
                  email !== '' && !isValidEmail(email)
                    ? t('auth.forgot.emailValid')
                    : ''
                }
              />

              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={loading || !email || !isValidEmail(email)}
                sx={{
                  backgroundColor: '#f8f8f8',
                  color: '#000',
                  height: '48px',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  mb: 3,
                  boxShadow: 'none',
                  border: '1px solid #e0e0e0',
                  '&:hover': {
                    backgroundColor: '#ebebeb',
                    boxShadow: 'none',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#f8f8f8',
                    color: '#999',
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t('auth.forgot.sendLink')
                )}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <Link
                  to="/login"
                  style={{
                    color: '#000',
                    textDecoration: 'none',
                    fontSize: '0.75rem',
                    fontWeight: 400,
                  }}
                >
                  {t('auth.forgot.return')}
                </Link>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Right Column - Image */}
        <Box
          sx={{
            width: { xs: '0%', md: '50%' },
            display: { xs: 'none', md: 'block' },
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(180deg, #4a6fa5 0%, #5b7db8 15%, #d97c91 45%, #e85d72 65%, #1a1a1a 100%)',
          }}
        >
          {/* Overlay shapes */}
          <Box
            sx={{
              position: 'absolute',
              top: '15%',
              right: '10%',
              width: '300px',
              height: '400px',
              background: 'linear-gradient(135deg, rgba(255, 100, 120, 0.6) 0%, rgba(255, 100, 120, 0.3) 100%)',
              filter: 'blur(80px)',
              transform: 'rotate(-15deg)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '25%',
              left: '15%',
              width: '250px',
              height: '350px',
              background: 'linear-gradient(135deg, rgba(255, 100, 120, 0.5) 0%, rgba(255, 100, 120, 0.2) 100%)',
              filter: 'blur(70px)',
              transform: 'rotate(25deg)',
            }}
          />

          {/* Trust badges - Center */}
          <Box sx={{ 
            position: 'absolute',
            top: '50%',
            left: '55%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 3.5,
            width: '85%',
            maxWidth: '420px',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Box sx={{ 
                fontSize: '2rem', 
                lineHeight: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                width: '56px',
                height: '56px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}>
                📧
              </Box>
              <Box>
                <Typography sx={{ 
                  fontSize: '1.125rem', 
                  fontWeight: 600, 
                  color: '#ffffff',
                  mb: 0.5,
                  lineHeight: 1.3,
                }}>
                  {t('auth.forgot.cardTitle1')}
                </Typography>
                <Typography sx={{ 
                  fontSize: '0.875rem', 
                  color: 'rgba(255, 255, 255, 0.75)',
                  lineHeight: 1.5,
                }}>
                  {t('auth.forgot.cardSubtitle1')}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Box sx={{ 
                fontSize: '2rem', 
                lineHeight: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                width: '56px',
                height: '56px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}>
                🔒
              </Box>
              <Box>
                <Typography sx={{ 
                  fontSize: '1.125rem', 
                  fontWeight: 600, 
                  color: '#ffffff',
                  mb: 0.5,
                  lineHeight: 1.3,
                }}>
                  {t('auth.forgot.cardTitle2')}
                </Typography>
                <Typography sx={{ 
                  fontSize: '0.875rem', 
                  color: 'rgba(255, 255, 255, 0.75)',
                  lineHeight: 1.5,
                }}>
                  {t('auth.forgot.cardSubtitle2')}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Box sx={{ 
                fontSize: '2rem', 
                lineHeight: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                width: '56px',
                height: '56px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}>
                ✅
              </Box>
              <Box>
                <Typography sx={{ 
                  fontSize: '1.125rem', 
                  fontWeight: 600, 
                  color: '#ffffff',
                  mb: 0.5,
                  lineHeight: 1.3,
                }}>
                  {t('auth.forgot.cardTitle3')}
                </Typography>
                <Typography sx={{ 
                  fontSize: '0.875rem', 
                  color: 'rgba(255, 255, 255, 0.75)',
                  lineHeight: 1.5,
                }}>
                  {t('auth.forgot.cardSubtitle3')}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Box sx={{ 
                fontSize: '2rem', 
                lineHeight: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                width: '56px',
                height: '56px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}>
                🛡️
              </Box>
              <Box>
                <Typography sx={{ 
                  fontSize: '1.125rem', 
                  fontWeight: 600, 
                  color: '#ffffff',
                  mb: 0.5,
                  lineHeight: 1.3,
                }}>
                  {t('auth.forgot.cardTitle4')}
                </Typography>
                <Typography sx={{ 
                  fontSize: '0.875rem', 
                  color: 'rgba(255, 255, 255, 0.75)',
                  lineHeight: 1.5,
                }}>
                  {t('auth.forgot.cardSubtitle4')}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {/* Bottom attribution */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 32,
              left: 32,
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1.5px solid rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(10px)',
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: '#ffffff',
                fontSize: '0.6875rem',
                fontWeight: 400,
                letterSpacing: '0.01em',
                opacity: 0.9,
              }}
            >
              {t('auth.forgot.message')}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ForgotPassword;